import React, { useState, useCallback, useMemo } from 'react';
import { DAYS, DAY_LABELS, formatHourFull } from './demand-planner-helpers';
import './BulkEditToolbarComponent.scss';

const WEEKDAY_INDICES = [0, 1, 2, 3, 4]; // Mon-Fri
const WEEKEND_INDICES = [5, 6]; // Sat-Sun

const BulkEditToolbarComponent = ({
  activeDay = 0,
  roles = [],
  enabledRoles = [],
  usersByRole,
  onBulkCopyDay,
  onBulkFillRange,
  onBulkUndo,
  canBulkUndo = false,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  // Copy Day state
  const [copySourceDay, setCopySourceDay] = useState(activeDay);
  const [copyTargetDays, setCopyTargetDays] = useState([]);

  // Fill Range state
  const [fillRoleId, setFillRoleId] = useState('');
  const [fillStartHour, setFillStartHour] = useState(0);
  const [fillEndHour, setFillEndHour] = useState(23);
  const [fillValue, setFillValue] = useState(0);
  const [fillTargetDays, setFillTargetDays] = useState([]);

  // Sync copySourceDay when activeDay changes
  React.useEffect(() => {
    setCopySourceDay(activeDay);
  }, [activeDay]);

  // Set default fill role when enabledRoles changes
  React.useEffect(() => {
    if (enabledRoles.length > 0 && !enabledRoles.includes(fillRoleId)) {
      setFillRoleId(enabledRoles[0]);
    }
  }, [enabledRoles, fillRoleId]);

  // --- Copy Day handlers ---

  const toggleCopyTarget = useCallback((dayIndex) => {
    setCopyTargetDays((previous) =>
      previous.includes(dayIndex)
        ? previous.filter((d) => d !== dayIndex)
        : [...previous, dayIndex]
    );
  }, []);

  const selectCopyPreset = useCallback((preset) => {
    setCopyTargetDays((previous) => {
      if (preset === 'weekdays') {
        const allSelected = WEEKDAY_INDICES.every((i) => previous.includes(i));

        if (allSelected) {
          return previous.filter((d) => !WEEKDAY_INDICES.includes(d));
        }

        return [...new Set([...previous, ...WEEKDAY_INDICES])];
      }

      if (preset === 'weekend') {
        const allSelected = WEEKEND_INDICES.every((i) => previous.includes(i));

        if (allSelected) {
          return previous.filter((d) => !WEEKEND_INDICES.includes(d));
        }

        return [...new Set([...previous, ...WEEKEND_INDICES])];
      }

      if (preset === 'all') {
        const allIndices = DAYS.map((_, i) => i);
        const allSelected = allIndices.every((i) => previous.includes(i));

        return allSelected ? [] : allIndices;
      }

      return previous;
    });
  }, []);

  const handleCopyApply = useCallback(() => {
    if (copyTargetDays.length === 0 || !onBulkCopyDay) return;
    const sourceDay = DAYS[copySourceDay];
    const targetDays = copyTargetDays.map((i) => DAYS[i]);
    onBulkCopyDay(sourceDay, targetDays);
    setCopyTargetDays([]);
  }, [copySourceDay, copyTargetDays, onBulkCopyDay]);

  // --- Fill Range handlers ---

  const toggleFillTarget = useCallback((dayIndex) => {
    setFillTargetDays((previous) =>
      previous.includes(dayIndex)
        ? previous.filter((d) => d !== dayIndex)
        : [...previous, dayIndex]
    );
  }, []);

  const selectFillPreset = useCallback((preset) => {
    setFillTargetDays((previous) => {
      if (preset === 'weekdays') {
        const allSelected = WEEKDAY_INDICES.every((i) => previous.includes(i));

        if (allSelected) {
          return previous.filter((d) => !WEEKDAY_INDICES.includes(d));
        }

        return [...new Set([...previous, ...WEEKDAY_INDICES])];
      }

      if (preset === 'weekend') {
        const allSelected = WEEKEND_INDICES.every((i) => previous.includes(i));

        if (allSelected) {
          return previous.filter((d) => !WEEKEND_INDICES.includes(d));
        }

        return [...new Set([...previous, ...WEEKEND_INDICES])];
      }

      if (preset === 'all') {
        const allIndices = DAYS.map((_, i) => i);
        const allSelected = allIndices.every((i) => previous.includes(i));

        return allSelected ? [] : allIndices;
      }

      return previous;
    });
  }, []);

  const handleFillApply = useCallback(() => {
    if (fillTargetDays.length === 0 || !fillRoleId || !onBulkFillRange) return;
    const targetDays = fillTargetDays.map((i) => DAYS[i]);
    onBulkFillRange(
      fillRoleId,
      fillStartHour,
      fillEndHour,
      fillValue,
      targetDays
    );
    setFillTargetDays([]);
  }, [
    fillRoleId,
    fillStartHour,
    fillEndHour,
    fillValue,
    fillTargetDays,
    onBulkFillRange,
  ]);

  const handleStartHourChange = useCallback(
    (hour) => {
      setFillStartHour(hour);
      if (hour > fillEndHour) {
        setFillEndHour(hour);
      }
    },
    [fillEndHour]
  );

  const handleEndHourChange = useCallback(
    (hour) => {
      setFillEndHour(hour);
      if (hour < fillStartHour) {
        setFillStartHour(hour);
      }
    },
    [fillStartHour]
  );

  // --- Hour options for dropdowns ---
  const hourOptions = useMemo(() => {
    const options = [];
    for (let h = 0; h < 24; h++) {
      options.push({ value: h, label: formatHourFull(h) });
    }

    return options;
  }, []);

  const enabledRoleObjects = useMemo(
    () => roles.filter((r) => enabledRoles.includes(r.id)),
    [roles, enabledRoles]
  );

  return (
    <div className="bulk-edit-toolbar">
      <button
        className="bulk-edit-toggle"
        type="button"
        onClick={() => setIsExpanded((p) => !p)}
      >
        <span className="toggle-label">Bulk Edit Tools</span>
        <span className={`toggle-chevron ${isExpanded ? 'expanded' : ''}`}>
          &#9662;
        </span>
      </button>

      {isExpanded && (
        <div className="bulk-edit-sections">
          {/* Fill Range Section */}
          <div className="bulk-section">
            <div className="section-header">Fill Range</div>
            <div className="section-body">
              <div className="field-row">
                <label className="field-label" htmlFor="fill-role">
                  Role
                </label>
                <select
                  className="field-select"
                  id="fill-role"
                  value={fillRoleId}
                  onChange={(event_) => {
                    setFillRoleId(Number(event_.target.value));
                  }}
                >
                  {enabledRoleObjects.map((role) => {
                    const count = (usersByRole?.byRole?.[role.id] || []).length;
                    return (
                      <option key={role.id} value={role.id}>
                        {role.name} ({count})
                      </option>
                    );
                  })}
                </select>
              </div>

              <div className="field-row field-row-inline">
                <div className="field-group">
                  <label className="field-label" htmlFor="fill-start-hour">
                    Start
                  </label>
                  <select
                    className="field-select"
                    id="fill-start-hour"
                    value={fillStartHour}
                    onChange={(event_) => {
                      handleStartHourChange(Number(event_.target.value));
                    }}
                  >
                    {hourOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="field-group">
                  <label className="field-label" htmlFor="fill-end-hour">
                    End
                  </label>
                  <select
                    className="field-select"
                    id="fill-end-hour"
                    value={fillEndHour}
                    onChange={(event_) => {
                      handleEndHourChange(Number(event_.target.value));
                    }}
                  >
                    {hourOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="field-row">
                <label className="field-label" htmlFor="fill-value">
                  Headcount
                </label>
                <input
                  className="field-input"
                  id="fill-value"
                  max={99}
                  min={0}
                  type="number"
                  value={fillValue}
                  onChange={(event_) => {
                    const v = Number.parseInt(event_.target.value, 10);
                    if (!Number.isNaN(v)) {
                      setFillValue(Math.max(0, Math.min(99, v)));
                    }
                  }}
                />
              </div>

              <div className="field-row">
                <span className="field-label">Target days</span>
                <div className="day-checkboxes">
                  {DAYS.map((day, index) => (
                    <label key={day} className="day-checkbox">
                      <input
                        checked={fillTargetDays.includes(index)}
                        type="checkbox"
                        onChange={() => toggleFillTarget(index)}
                      />
                      <span className="day-checkbox-label">
                        {DAY_LABELS[index]}
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="quick-select-row">
                <button
                  className="quick-select-btn"
                  type="button"
                  onClick={() => selectFillPreset('weekdays')}
                >
                  Weekdays
                </button>
                <button
                  className="quick-select-btn"
                  type="button"
                  onClick={() => selectFillPreset('weekend')}
                >
                  Weekend
                </button>
                <button
                  className="quick-select-btn"
                  type="button"
                  onClick={() => selectFillPreset('all')}
                >
                  All
                </button>
              </div>

              <button
                className="bulk-apply-btn"
                disabled={fillTargetDays.length === 0}
                type="button"
                onClick={handleFillApply}
              >
                Apply Fill
              </button>
            </div>
          </div>

          {/* Copy Day Section */}
          <div className="bulk-section">
            <div className="section-header">Copy Day</div>
            <div className="section-body">
              <div className="field-row">
                <label className="field-label" htmlFor="copy-source-day">
                  Source day
                </label>
                <select
                  className="field-select"
                  id="copy-source-day"
                  value={copySourceDay}
                  onChange={(event_) => {
                    setCopySourceDay(Number(event_.target.value));
                  }}
                >
                  {DAYS.map((day, index) => (
                    <option key={day} value={index}>
                      {day}
                    </option>
                  ))}
                </select>
              </div>

              <div className="field-row">
                <span className="field-label">Target days</span>
                <div className="day-checkboxes">
                  {DAYS.map((day, index) => (
                    <label key={day} className="day-checkbox">
                      <input
                        checked={copyTargetDays.includes(index)}
                        type="checkbox"
                        onChange={() => toggleCopyTarget(index)}
                      />
                      <span className="day-checkbox-label">
                        {DAY_LABELS[index]}
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="quick-select-row">
                <button
                  className="quick-select-btn"
                  type="button"
                  onClick={() => selectCopyPreset('weekdays')}
                >
                  Weekdays
                </button>
                <button
                  className="quick-select-btn"
                  type="button"
                  onClick={() => selectCopyPreset('weekend')}
                >
                  Weekend
                </button>
                <button
                  className="quick-select-btn"
                  type="button"
                  onClick={() => selectCopyPreset('all')}
                >
                  All
                </button>
              </div>

              <button
                className="bulk-apply-btn"
                disabled={copyTargetDays.length === 0}
                type="button"
                onClick={handleCopyApply}
              >
                Apply Copy
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Undo bar */}
      {canBulkUndo && (
        <div className="bulk-undo-bar">
          <span className="undo-message">Bulk edit applied</span>
          <button className="undo-btn" type="button" onClick={onBulkUndo}>
            Undo last bulk edit
          </button>
        </div>
      )}
    </div>
  );
};

export default React.memo(BulkEditToolbarComponent);
